import {
  BusinessUnit,
  BusinessUnitDraft,
  BusinessUnitUpdate,
  BusinessUnitCostMetrics,
  MessageToNotify,
} from 'services/types/businessUnit';
import { TemplateResponse } from 'services/types/message';

import baseApi from './baseApi';

export type HeaderComponent = {
  type: 'HEADER';
  text?: string;
  format: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  example?: {
    header_handle?: string[];
  };
};

export type BodyComponent = {
  type: 'BODY';
  text: string;
  example?: {
    body_text: string[][];
  };
};

export type FooterComponent = {
  type: 'FOOTER';
  text: string;
};

export type WhatsAppButton = { type: 'QUICK_REPLY'; text: string };
export type ButtonsComponent = {
  type: 'BUTTONS';
  buttons: Array<WhatsAppButton>;
};

export type TemplateComponent = HeaderComponent | BodyComponent | FooterComponent | ButtonsComponent;

export interface TemplateCreate {
  name: string;
  category: 'UTILITY' | 'MARKETING';
  language: 'es';
  components: TemplateComponent[];
}

const businessUnitsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getBusinessUnits: build.query({
      query: () => ({
        url: '/admin/businessUnits',
        method: 'GET',
      }),
      transformResponse: (response: BusinessUnit[]) => response,
    }),
    getBusinessUnit: build.query({
      query: (id: number) => ({
        url: `/admin/businessUnits/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: BusinessUnit) => response,
    }),
    editBusinessUnit: build.mutation({
      query: (props: { businessUnit: BusinessUnitUpdate; id: number }) => ({
        url: `admin/businessUnits/${props.id}`,
        method: 'PATCH',
        body: props.businessUnit,
      }),
      transformResponse: (response: BusinessUnit) => response,
    }),
    createBusinessUnit: build.mutation({
      query: (company: BusinessUnitDraft) => ({
        url: `admin/businessUnits`,
        method: 'POST',
        body: company,
      }),
      transformResponse: (response: BusinessUnit) => response,
    }),
    deleteBusinessUnitImage: build.mutation({
      query: (props: { businessUnitId: number; fileName: string }) => ({
        url: `/admin/businessUnits/${props.businessUnitId}/images/${props.fileName}`,
        method: 'DELETE',
      }),
      transformResponse: (response) => response,
    }),
    uploadBusinessUnitImages: build.mutation({
      query: (props: { businessUnitId: number; files: File[] }) => {
        const formData = new FormData();
        props.files.forEach((file) => {
          formData.append('file', file);
        });
        return {
          url: `/admin/businessUnits/${props.businessUnitId}/images`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response) => response,
    }),
    adminGetBusinessUnitCostCharts: build.query({
      query: (id: number) => ({
        url: `/admin/businessUnits/${id}/cost-charts`,
        method: 'GET',
      }),
      transformResponse: (response: BusinessUnitCostMetrics) => response,
    }),
    getBusinessUnitTemplates: build.mutation({
      query: (id: number) => ({
        url: `/businessUnits/${id}/templates`,
        method: 'GET',
      }),
      transformResponse: (response: TemplateResponse) => response,
    }),
    editBusinessUnitMessagesToNotify: build.mutation({
      query: (props: { messagesToNotify: MessageToNotify[]; id: number }) => ({
        url: `admin/businessUnits/messages-to-notify/${props.id}`,
        method: 'PATCH',
        body: props.messagesToNotify,
      }),
      transformResponse: (response: BusinessUnit) => response,
    }),
    createBusinessUnitTemplate: build.mutation({
      query: (props: { businessUnitId: number; template: TemplateCreate }) => ({
        url: `businessUnits/${props.businessUnitId}/template`,
        method: 'POST',
        body: props.template,
      }),
      transformResponse: (response: TemplateResponse) => response,
    }),
    uploadTemplateFile: build.mutation({
      query: (props: { businessUnitId: number; file: File }) => {
        const formData = new FormData();
        formData.append('file', props.file);
        return {
          url: `businessUnits/${props.businessUnitId}/template-file`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: { url: string }) => response,
    }),
    getUserBusinessUnits: build.query({
      query: () => ({
        url: '/businessUnits/currentUserBusinessUnits',
        method: 'GET',
      }),
      transformResponse: (response: BusinessUnit[]): BusinessUnit[] => response,
    }),
    getBusinessUnitById: build.mutation({
      query: (businessUnitId: number) => ({
        url: `/businessUnits/businessUnit/${businessUnitId}`,
        method: 'GET',
      }),
      transformResponse: (response: BusinessUnit): BusinessUnit => response,
    }),
    getBusinessUnitSources: build.query({
      query: (props: { businessUnitId: number | null }) => ({
        url: `/businessUnits/businessUnit/sources/${props.businessUnitId || ''}`,
        method: 'GET',
      }),
      transformResponse: (response: string[]): string[] => response,
    }),
    getBusinessUnitBrands: build.query({
      query: (props: { businessUnitId: number | null }) => ({
        url: `/businessUnits/businessUnit/brands/${props.businessUnitId || ''}`,
        method: 'GET',
      }),
      transformResponse: (response: string[]): string[] => response,
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetBusinessUnitsQuery,
  useGetBusinessUnitQuery,
  useEditBusinessUnitMutation,
  useCreateBusinessUnitMutation,
  useDeleteBusinessUnitImageMutation,
  useUploadBusinessUnitImagesMutation,
  useAdminGetBusinessUnitCostChartsQuery,
  useGetBusinessUnitTemplatesMutation,
  useEditBusinessUnitMessagesToNotifyMutation,
  useCreateBusinessUnitTemplateMutation,
  useUploadTemplateFileMutation,
  useGetUserBusinessUnitsQuery,
  useGetBusinessUnitByIdMutation,
  useGetBusinessUnitSourcesQuery,
  useGetBusinessUnitBrandsQuery,
} = businessUnitsApi;
